import React, { useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';

export default function CustomerEngagementPage() {
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('');
  const [customEmail, setCustomEmail] = useState({
    subject: '',
    message: ''
  });

  // Mock customer data - simplified
  const customerData = [
    {
      id: 1,
      name: "Sarah Mitchell",
      email: "sarah@example.com",
      phone: "+44 7123 456789",
      lastEvent: "Coffee Networking",
      lastAttended: "2025-01-20"
    },
    {
      id: 2,
      name: "James Wilson",
      email: "james@example.com",
      phone: "+44 7234 567890",
      lastEvent: "Digital Marketing Workshop",
      lastAttended: "2025-01-18"
    },
    {
      id: 3,
      name: "Emma Thompson",
      email: "emma@example.com",
      phone: "", // No phone number
      lastEvent: "Book Reading Evening",
      lastAttended: "2025-01-15"
    },
    {
      id: 4,
      name: "Michael Chen",
      email: "michael@example.com",
      phone: "+44 7456 789012",
      lastEvent: "Business Finance Workshop",
      lastAttended: "2024-12-08"
    },
    {
      id: 5,
      name: "Lucy Green",
      email: "lucy@example.com",
      phone: "+44 7567 890123",
      lastEvent: "Sustainable Business Meetup",
      lastAttended: "2025-01-10"
    },
    {
      id: 6,
      name: "David Brown",
      email: "david@example.com",
      phone: "", // No phone number
      lastEvent: "Tech Innovation Showcase",
      lastAttended: "2025-01-12"
    }
  ];

  // Email templates - expanded with all marketing options
  const emailTemplates = {
    thankyou: {
      subject: "Thank you for attending [LAST_EVENT]! 🌟",
      message: `Hi [NAME],

Thank you for attending [LAST_EVENT]! It was wonderful having you there.

Your participation made the event special and I hope you found it valuable.

Looking forward to seeing you at future events!

Best regards,
Sarah Mitchell
The Coffee House`
    },
    followup: {
      subject: "Great seeing you at [LAST_EVENT]! ☕",
      message: `Hi [NAME],

I hope you enjoyed [LAST_EVENT] as much as we did!

I wanted to follow up and see if you have any questions or feedback about the event.

We have some exciting events coming up that I think you'd love. I'll keep you posted!

Best regards,
Sarah Mitchell
The Coffee House`
    },
    newevent: {
      subject: "New Event Alert: You're Invited! 🎉",
      message: `Hi [NAME],

I'm excited to invite you to our next event!

[EVENT_TITLE]
📅 Date: [EVENT_DATE]
🕐 Time: [EVENT_TIME]
📍 Location: [EVENT_LOCATION]
💰 Price: [EVENT_PRICE]

Based on your interest in [LAST_EVENT], I think you'll really enjoy this one.

Space is limited, so reserve your spot today!

Best regards,
Sarah Mitchell
The Coffee House`
    },
    promotion: {
      subject: "Special Offer Just for You! 🎁",
      message: `Hi [NAME],

As a valued member of our community, I wanted to share this exclusive offer with you:

🎁 20% OFF your next event booking
✨ Valid until [EXPIRY_DATE]
🎫 Use code: VALUED20

This offer is my way of saying thank you for being part of our community since [LAST_EVENT].

Book your next event and save!

Best regards,
Sarah Mitchell
The Coffee House`
    },
    discount: {
      subject: "Limited Time: 50% Off Early Bird Special! ⚡",
      message: `Hi [NAME],

Early Bird Special Alert! 🐦

Get 50% OFF our upcoming events when you book in the next 48 hours:

📅 Available for all events this month
⏰ Offer expires in 48 hours
🎫 Automatic discount applied at checkout

Since you enjoyed [LAST_EVENT], I didn't want you to miss this amazing deal.

Book now before spots fill up!

Best regards,
Sarah Mitchell
The Coffee House`
    },
    newsletter: {
      subject: "Monthly Update: What's Coming Up! 📬",
      message: `Hi [NAME],

Here's what's happening at The Coffee House this month:

🗓️ UPCOMING EVENTS:
• Workshop Series: Digital Marketing (Jan 27)
• Networking Breakfast (Feb 3)
• Book Club Discussion (Feb 10)

🌟 COMMUNITY HIGHLIGHTS:
• 50+ new members joined this month
• Featured in Nottingham Business Weekly

💡 MEMBER SPOTLIGHT:
Thank you to everyone who attended [LAST_EVENT] - your feedback helps us improve!

Stay connected and keep growing with us!

Best regards,
Sarah Mitchell
The Coffee House`
    },
    winback: {
      subject: "We miss you! Come back with a special welcome 💙",
      message: `Hi [NAME],

It's been a while since we last saw you at [LAST_EVENT], and we genuinely miss having you in our community!

I wanted to personally invite you back with a special welcome offer:

🎁 Your next event is completely FREE
✨ No strings attached
🤝 Just mention this email when you attend

We've added some exciting new events that I think you'd love, including workshops, networking sessions, and cultural events.

Would love to see you again soon!

Warm regards,
Sarah Mitchell
The Coffee House`
    },
    feedback: {
      subject: "How was [LAST_EVENT]? We'd love your feedback! 💭",
      message: `Hi [NAME],

Thank you for attending [LAST_EVENT]! 

I hope you had a great experience and made some valuable connections.

Could you spare 2 minutes to share your feedback? It really helps us improve future events:

⭐ Rate your experience: [FEEDBACK_LINK]
💬 What did you enjoy most?
💡 Any suggestions for improvement?

Your input shapes our community, and I personally read every response.

Thank you for helping us grow!

Best regards,
Sarah Mitchell
The Coffee House`
    },
    personal: {
      subject: "",
      message: ""
    }
  };

  const handleCustomerSelect = (customerId) => {
    setSelectedCustomers(prev => 
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === customerData.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(customerData.map(c => c.id));
    }
  };

  const handleQuickAction = (customerId, action) => {
    setSelectedCustomers([customerId]);
    setEmailTemplate(action);
    setEmailModalOpen(true);
  };

  const handleSendEmail = () => {
    if (selectedCustomers.length === 0) {
      alert('Please select at least one customer');
      return;
    }
    setEmailTemplate('personal');
    setEmailModalOpen(true);
  };

  const personalizeEmail = (template, customer) => {
    let subject = template.subject;
    let message = template.message;

    subject = subject.replace(/\[NAME\]/g, customer.name);
    subject = subject.replace(/\[LAST_EVENT\]/g, customer.lastEvent);
    
    message = message.replace(/\[NAME\]/g, customer.name);
    message = message.replace(/\[LAST_EVENT\]/g, customer.lastEvent);

    return { subject, message };
  };

  const submitEmail = async () => {
    try {
      // Simulate email sending
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      alert(`Email sent successfully to ${selectedCustomers.length} customer${selectedCustomers.length !== 1 ? 's' : ''}!`);
      setEmailModalOpen(false);
      setEmailTemplate('');
      setCustomEmail({ subject: '', message: '' });
      setSelectedCustomers([]);
    } catch (error) {
      alert('Failed to send email. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Customer Engagement</h1>
          <p className="text-slate-600">
            Connect with your customers and build lasting relationships
          </p>
        </div>

        {/* Quick Email Templates Section */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-8">
          <div className="p-6 border-b border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Quick Email Campaigns</h2>
                <p className="text-sm text-slate-600">Send marketing emails to all or selected customers</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                onClick={() => {
                  setSelectedCustomers(customerData.map(c => c.id));
                  setEmailTemplate('newevent');
                  setEmailModalOpen(true);
                }}
                className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors text-left group"
              >
                <div className="flex items-center space-x-3 mb-2">
                  <i className="fas fa-calendar-plus text-blue-600 group-hover:text-blue-700"></i>
                  <h3 className="font-semibold text-slate-900">New Event</h3>
                </div>
                <p className="text-sm text-slate-600">Invite customers to new events</p>
              </button>

              <button
                onClick={() => {
                  setSelectedCustomers(customerData.map(c => c.id));
                  setEmailTemplate('promotion');
                  setEmailModalOpen(true);
                }}
                className="p-4 border border-slate-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors text-left group"
              >
                <div className="flex items-center space-x-3 mb-2">
                  <i className="fas fa-gift text-purple-600 group-hover:text-purple-700"></i>
                  <h3 className="font-semibold text-slate-900">Promotion</h3>
                </div>
                <p className="text-sm text-slate-600">Send special offers and deals</p>
              </button>

              <button
                onClick={() => {
                  setSelectedCustomers(customerData.map(c => c.id));
                  setEmailTemplate('discount');
                  setEmailModalOpen(true);
                }}
                className="p-4 border border-slate-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors text-left group"
              >
                <div className="flex items-center space-x-3 mb-2">
                  <i className="fas fa-percentage text-green-600 group-hover:text-green-700"></i>
                  <h3 className="font-semibold text-slate-900">Discount</h3>
                </div>
                <p className="text-sm text-slate-600">Share discount codes and early bird offers</p>
              </button>

              <button
                onClick={() => {
                  setSelectedCustomers(customerData.map(c => c.id));
                  setEmailTemplate('newsletter');
                  setEmailModalOpen(true);
                }}
                className="p-4 border border-slate-200 rounded-lg hover:border-yellow-300 hover:bg-yellow-50 transition-colors text-left group"
              >
                <div className="flex items-center space-x-3 mb-2">
                  <i className="fas fa-newspaper text-yellow-600 group-hover:text-yellow-700"></i>
                  <h3 className="font-semibold text-slate-900">Newsletter</h3>
                </div>
                <p className="text-sm text-slate-600">Monthly updates and community news</p>
              </button>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Your Customers</h2>
                <p className="text-sm text-slate-600">Manage and engage with event attendees</p>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-slate-600">
                  {selectedCustomers.length} of {customerData.length} selected
                </span>
                <button
                  onClick={handleSendEmail}
                  disabled={selectedCustomers.length === 0}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-400 disabled:to-slate-400 text-white rounded-lg text-sm font-medium transition-all disabled:cursor-not-allowed"
                >
                  <i className="fas fa-envelope mr-2"></i>
                  Send Email ({selectedCustomers.length})
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedCustomers.length === customerData.length}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Last Event
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {customerData.map((customer) => (
                  <tr key={customer.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer.id)}
                        onChange={() => handleCustomerSelect(customer.id)}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                          <span className="text-sm font-medium text-white">
                            {customer.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-900">{customer.name}</div>
                          <div className="text-sm text-slate-600">{customer.email}</div>
                          {customer.phone && (
                            <div className="text-sm text-slate-500">{customer.phone}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{customer.lastEvent}</div>
                        <div className="text-sm text-slate-600">
                          {new Date(customer.lastAttended).toLocaleDateString('en-GB', { 
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="text-xs text-slate-500">
                          {formatDate(customer.lastAttended)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            handleQuickAction(customer.id, e.target.value);
                            e.target.value = ''; // Reset dropdown
                          }
                        }}
                        className="px-3 py-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Choose action...</option>
                        <option value="thankyou">Send Thank You</option>
                        <option value="followup">Send Follow-up</option>
                        <option value="newevent">New Event Invite</option>
                        <option value="promotion">Send Promotion</option>
                        <option value="discount">Send Discount</option>
                        <option value="newsletter">Monthly Newsletter</option>
                        <option value="winback">Win Back Offer</option>
                        <option value="feedback">Request Feedback</option>
                        <option value="personal">Personal Email</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {customerData.length === 0 && (
            <div className="text-center py-12">
              <div className="w-24 h-24 bg-slate-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <i className="fas fa-users text-slate-400 text-3xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No customers yet</h3>
              <p className="text-slate-600 mb-6">
                Start hosting events to build your customer database
              </p>
              <button
                onClick={() => window.location.href = '/create-event'}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-all"
              >
                Create Your First Event
              </button>
            </div>
          )}
        </div>

        {/* Email Modal */}
        {emailModalOpen && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
              {/* Modal Header */}
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">
                    Send Email to {selectedCustomers.length} Customer{selectedCustomers.length !== 1 ? 's' : ''}
                  </h3>
                  <button
                    onClick={() => {
                      setEmailModalOpen(false);
                      setEmailTemplate('');
                      setCustomEmail({ subject: '', message: '' });
                    }}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
              
              {/* Modal Body - Scrollable */}
              <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
                {/* Show template info */}
                {emailTemplate !== 'personal' && emailTemplate && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-1">
                      {emailTemplate === 'thankyou' && 'Thank You Email'}
                      {emailTemplate === 'followup' && 'Follow-up Email'}
                      {emailTemplate === 'newevent' && 'New Event Invitation'}
                      {emailTemplate === 'promotion' && 'Promotional Email'}
                      {emailTemplate === 'discount' && 'Discount Offer'}
                      {emailTemplate === 'newsletter' && 'Monthly Newsletter'}
                      {emailTemplate === 'winback' && 'Win Back Campaign'}
                      {emailTemplate === 'feedback' && 'Feedback Request'}
                    </h4>
                    <p className="text-sm text-blue-800">
                      This email will be personalized with each customer's name and event details.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Subject</label>
                  <input
                    type="text"
                    value={
                      emailTemplate === 'personal' 
                        ? customEmail.subject 
                        : emailTemplates[emailTemplate]?.subject || ''
                    }
                    onChange={(e) => {
                      if (emailTemplate === 'personal') {
                        setCustomEmail(prev => ({ ...prev, subject: e.target.value }));
                      }
                    }}
                    className="w-full px-3 py-3 border border-slate-300 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter email subject"
                    readOnly={emailTemplate !== 'personal'}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
                  <textarea
                    value={
                      emailTemplate === 'personal' 
                        ? customEmail.message 
                        : emailTemplates[emailTemplate]?.message || ''
                    }
                    onChange={(e) => {
                      if (emailTemplate === 'personal') {
                        setCustomEmail(prev => ({ ...prev, message: e.target.value }));
                      }
                    }}
                    rows={12}
                    className="w-full px-3 py-3 border border-slate-300 rounded-lg bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Enter your message..."
                    readOnly={emailTemplate !== 'personal'}
                  />
                </div>

                {emailTemplate !== 'personal' && emailTemplate && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="text-sm text-slate-600">
                      <i className="fas fa-info-circle mr-2"></i>
                      Placeholders like [NAME], [LAST_EVENT], [EVENT_TITLE] will be automatically replaced for each customer.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Modal Footer - Fixed */}
              <div className="p-6 border-t border-slate-200 flex justify-end space-x-3 bg-slate-50">
                <button
                  onClick={() => {
                    setEmailModalOpen(false);
                    setEmailTemplate('');
                    setCustomEmail({ subject: '', message: '' });
                  }}
                  className="px-6 py-3 border border-slate-300 text-slate-700 hover:text-slate-900 hover:border-slate-400 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitEmail}
                  disabled={
                    emailTemplate === 'personal' && 
                    (!customEmail.subject.trim() || !customEmail.message.trim())
                  }
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-slate-400 disabled:to-slate-400 text-white rounded-lg font-medium transition-all disabled:cursor-not-allowed"
                >
                  <i className="fas fa-send mr-2"></i>
                  Send Email
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}